const RIFT_17TRACK_SCRIPT_URL = 'https://www.17track.net/externalcall.js';
const RIFT_17TRACK_TIMEOUT = 10000;

let rift17TrackScriptPromise;

function has17TrackApi() {
  return typeof window.YQV5?.trackSingle === 'function';
}

function load17TrackScript() {
  if (has17TrackApi()) return Promise.resolve();
  if (rift17TrackScriptPromise) return rift17TrackScriptPromise;

  rift17TrackScriptPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(
      `script[src="${RIFT_17TRACK_SCRIPT_URL}"], script[src="//www.17track.net/externalcall.js"]`
    );

    if (!script) {
      script = document.createElement('script');
      script.src = RIFT_17TRACK_SCRIPT_URL;
      script.async = true;
      script.dataset.rift17trackScript = 'true';
      document.head.append(script);
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (has17TrackApi()) {
        window.clearInterval(interval);
        window.clearTimeout(timeout);
        resolve();
      } else if (Date.now() - startedAt >= RIFT_17TRACK_TIMEOUT) {
        window.clearInterval(interval);
      }
    }, 50);

    const timeout = window.setTimeout(() => {
      window.clearInterval(interval);
      reject(new Error('17TRACK did not become available before timeout.'));
    }, RIFT_17TRACK_TIMEOUT);

    script.addEventListener(
      'error',
      () => {
        window.clearInterval(interval);
        window.clearTimeout(timeout);
        reject(new Error('17TRACK script failed to load.'));
      },
      { once: true }
    );
  }).catch((error) => {
    document.querySelector('script[data-rift17track-script="true"]')?.remove();
    rift17TrackScriptPromise = undefined;
    throw error;
  });

  return rift17TrackScriptPromise;
}

function resolve17TrackLocale(locale) {
  const normalized = String(locale || '')
    .trim()
    .toLowerCase()
    .replace('_', '-');

  if (normalized === 'zh-tw' || normalized === 'zh-hk') return 'zh-hk';
  if (normalized.startsWith('zh')) return 'zh-cn';

  const aliases = { nb: 'no' };
  const language = aliases[normalized.split('-')[0]] || normalized.split('-')[0];
  const supported = new Set([
    'az',
    'bg',
    'cs',
    'da',
    'de',
    'el',
    'en',
    'es',
    'fi',
    'fr',
    'hu',
    'it',
    'ja',
    'kk',
    'ko',
    'lt',
    'mk',
    'nl',
    'no',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'sq',
    'sr',
    'sv',
    'th',
    'tr',
    'uk',
  ]);

  return supported.has(language) ? language : 'en';
}

class RiftPackageTracking extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.form = this.querySelector('[data-rift-tracking-form]');
    this.input = this.querySelector('[data-rift-tracking-input]');
    this.button = this.querySelector('[data-rift-tracking-submit]');
    this.buttonLabel = this.querySelector('[data-rift-tracking-button-label]');
    this.buttonIcon = this.querySelector('[data-rift-tracking-button-icon]');
    this.buttonSpinner = this.querySelector('[data-rift-tracking-button-spinner]');
    this.message = this.querySelector('[data-rift-tracking-message]');
    this.liveRegion = this.querySelector('[data-rift-tracking-live]');
    this.retryButton = this.querySelector('[data-rift-tracking-retry]');
    this.results = this.querySelector('[data-rift-tracking-results]');

    if (!this.form || !this.input || !this.button || !this.results) {
      this.abortController = undefined;
      return;
    }

    const { signal } = this.abortController;
    this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
    this.input.addEventListener('input', () => this.clearValidationError(), { signal });
    this.retryButton?.addEventListener('click', () => this.form.requestSubmit(), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = undefined;
  }

  get copy() {
    return {
      required: this.dataset.errorRequired,
      length: this.dataset.errorLength,
      characters: this.dataset.errorCharacters,
      loading: this.dataset.loading,
      loaded: this.dataset.loaded,
      unavailable: this.dataset.serviceUnavailable,
    };
  }

  normalizeTrackingNumber() {
    const normalized = this.input.value.replace(/\s+/g, '');
    this.input.value = normalized;
    return normalized;
  }

  validate(trackingNumber) {
    if (!trackingNumber) return this.copy.required;
    if (trackingNumber.length < 5 || trackingNumber.length > 50) return this.copy.length;
    if (!/^[A-Za-z0-9-]+$/.test(trackingNumber)) return this.copy.characters;
    return '';
  }

  clearValidationError() {
    this.input.removeAttribute('aria-invalid');
    if (this.message?.dataset.kind === 'validation') this.setMessage();
  }

  setMessage(text = '', kind = '', showRetry = false) {
    if (this.message) {
      this.message.textContent = text;
      this.message.dataset.kind = kind;
      this.message.hidden = !text;
    }
    if (this.retryButton) this.retryButton.hidden = !showRetry;
  }

  announce(text) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      this.liveRegion.textContent = text;
    });
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));
    this.input.readOnly = isLoading;
    if (this.buttonLabel) this.buttonLabel.textContent = isLoading ? this.copy.loading : this.dataset.buttonLabel;
    if (this.buttonIcon) this.buttonIcon.hidden = isLoading;
    if (this.buttonSpinner) this.buttonSpinner.hidden = !isLoading;
  }

  async handleSubmit(event) {
    event.preventDefault();

    const trackingNumber = this.normalizeTrackingNumber();
    const validationError = this.validate(trackingNumber);

    if (validationError) {
      this.input.setAttribute('aria-invalid', 'true');
      this.setMessage(validationError, 'validation');
      this.announce(validationError);
      this.input.focus();
      return;
    }

    this.input.removeAttribute('aria-invalid');
    this.results.hidden = true;
    this.results.replaceChildren();
    this.setMessage(this.copy.loading, 'loading');
    this.announce(this.copy.loading);
    this.setLoading(true);

    try {
      await load17TrackScript();
      this.results.hidden = false;
      window.YQV5.trackSingle({
        YQ_ContainerId: this.results.id,
        YQ_Height: Number.parseInt(this.dataset.resultHeight, 10) || 560,
        YQ_Fc: '0',
        YQ_Lang: resolve17TrackLocale(this.dataset.locale),
        YQ_Num: trackingNumber,
      });
      this.setMessage();
      this.announce(this.copy.loaded);
    } catch {
      this.results.hidden = true;
      this.setMessage(this.copy.unavailable, 'error', true);
      this.announce(this.copy.unavailable);
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('rift-package-tracking')) {
  customElements.define('rift-package-tracking', RiftPackageTracking);
}
